import React, { ChangeEvent, useMemo, useRef, useState } from 'react'
import { Button, Input, Spin } from 'antd'
import Title from 'antd/lib/typography/Title'
import { AMAMode, AnnotationAMA, utilsAnnotation, utilsEditor } from '@amaspace-editor/editor-3d'
import ReactQuill from 'react-quill'
import Quill from 'quill'
import 'react-quill/dist/quill.snow.css'
import { AssetAnnotationType } from '../../../../api/assetApi'
import Utils from '../../../../utils'
//@ts-ignore
import ImageUploader from 'quill-image-uploader'
import 'quill-image-uploader/dist/quill.imageUploader.min.css'

Quill.register('modules/imageUploader', ImageUploader)

interface AnnotationsEditorProps {
    id: number | string
    data: AssetAnnotationType[]
    setData: React.Dispatch<React.SetStateAction<AssetAnnotationType[]>>
    isLoading: boolean
    updateAnnotation: (data: AssetAnnotationType) => Promise<any>
    createAnnotation: (data: AssetAnnotationType) => Promise<any>
}

const AnnotationsEditor = ({
    id,
    data,
    setData,
    isLoading,
    updateAnnotation,
    createAnnotation
}: AnnotationsEditorProps) => {
    const editor3d = utilsEditor()
    const annotationUtils = utilsAnnotation()
    const fileRef = useRef<HTMLInputElement | null>(null)
    const reactQuill = useRef<any>(null)
    const [htmlValue, setHtmlValue] = useState<string>('')

    const uploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e?.target?.files?.length) return

        Utils.uploadImage(e.target.files[0] as File).then(res => {
            const imageUrl = res.body.data
            const quillObj = reactQuill?.current?.getEditor()
            const range = quillObj?.getSelection()

            quillObj.insertEmbed(range.index, 'image', imageUrl)
        })
    }

    const modulesQuill = useMemo(
        () => ({
            toolbar: {
                container: [
                    [{ header: [1, 2, false] }],
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                    ['link', 'image', 'video'],
                    ['clean']
                ]
            },
            imageUploader: {
                upload: (file: File) => {
                    return Utils.uploadImage(file)
                }
                // upload: (file: File) => {
                //   return new Promise((resolve, reject) => {
                //     const formData = new FormData()
                //     formData.append('image', file)
                //
                //     fetch(
                //       'https://api.imgbb.com/1/upload?key=28e6571504d3090a4365a1a4e7590e88',
                //       {
                //         method: 'POST',
                //         body: formData
                //       }
                //     )
                //       .then(response => response.json())
                //       .then(result => {
                //         console.log(result)
                //         resolve(result.data.url)
                //       })
                //       .catch(error => {
                //         reject('Upload failed')
                //         console.error('Error:', error)
                //       })
                //   })
                // }
            },
            clipboard: {
                matchVisual: true
            }
        }),
        []
    )

    const handleSaveAnnotation = async () => {
        const annotation = data.filter(item => item.id === id)
        const { type } = annotation[0]
        annotationUtils.removeBlankAnnotation()
        if (type) {
            const filteredAnnotation = annotation.map(item => ({ ...item, value: htmlValue }))
            filteredAnnotation[0].position = JSON.stringify(editor3d?.newAnnotation?.position?.toArray())
            const { data } = await createAnnotation(filteredAnnotation[0])

            if (editor3d && data) {
                const final = {
                    ...filteredAnnotation[0],
                    id: data,
                    position: JSON.stringify(editor3d?.newAnnotation?.position?.toArray())
                } as any

                annotationUtils.createHotspot(new AnnotationAMA(final))
            }
        } else {
            const currentAnnotation = editor3d.getMeshById(annotation[0].id)
            const final: AssetAnnotationType = {
                ...annotation[0],
                position: JSON.stringify(currentAnnotation?.position?.toArray())
            }
            const res = await updateAnnotation(final)

            if (editor3d && res && currentAnnotation) {
                editor3d.updateObjectData(currentAnnotation, { value: annotation[0].value })
            }
        }
        editor3d.changeMode(AMAMode.VIEWER)
    }

    const handleChangeContent = (html: string) => {
        setHtmlValue(html)
        setData(prevState =>
            prevState.map(item => {
                if (item.id === id)
                    return {
                        ...item,
                        value: html
                    }

                return item
            })
        )
    }

    const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setData(prevState =>
            prevState.map(item => {
                if (item.id === id)
                    return {
                        ...item,
                        name: e.target.value
                    }
                return item
            })
        )
    }

    return (
        <>
            <Button onClick={handleSaveAnnotation} type="primary">
                Save Annotation
            </Button>
            {isLoading ? (
                <Spin size="large" />
            ) : (
                <>
                    <Input
                        style={{ margin: '16px 0' }}
                        placeholder="Annotation Name"
                        value={data.find(item => item.id === id)?.name}
                        onChange={handleChangeName}
                        maxLength={40}
                    />

                    <Title level={4} children="Annotation Content" />
                    <div style={{ height: '250px' }}>
                        <ReactQuill
                            style={{ height: '100%' }}
                            ref={reactQuill}
                            modules={modulesQuill}
                            onChange={handleChangeContent}
                            value={data.find(item => item.id === id)?.value}
                        />
                    </div>

                    <input onChange={uploadImage} ref={fileRef} type="file" style={{ display: 'none' }} />
                </>
            )}
        </>
    )
}

export default AnnotationsEditor
